import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { doc, getDoc, updateDoc, addDoc, collection, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { PortvilleRoom, PortvilleResidency, PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { downloadStorageFile } from '../../../utils/storageDownload';
import { buildResidentNumber, sanitizeResidentNumberPart, splitResidentNumber } from '../../../utils/residentNumber';

type LotteryDocumentType = 'identification' | 'employment' | 'other';
type LotteryDocuments = Partial<Record<LotteryDocumentType, string>>;

const getDocumentDisplayName = (docType: LotteryDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const ContractSignaturePage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  
  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState('');
  const [signatureAddress, setSignatureAddress] = useState('');
  const [signatureResidentNumberFront, setSignatureResidentNumberFront] = useState('');
  const [signatureResidentNumberBack, setSignatureResidentNumberBack] = useState('');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const [moveInDate, setMoveInDate] = useState(new Date().toISOString().split('T')[0]);
  const [contractEndDate, setContractEndDate] = useState('');
  const [signatureSize, setSignatureSize] = useState({ width: 400, height: 200 });
  const [isContractCancellation, setIsContractCancellation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [lotteryDocuments, setLotteryDocuments] = useState<LotteryDocuments>({});
  const [lotteryDocumentsLoading, setLotteryDocumentsLoading] = useState(false);
  const [lotteryDocumentsError, setLotteryDocumentsError] = useState<string | null>(null);
  const [lotteryDocumentsDownloading, setLotteryDocumentsDownloading] = useState<LotteryDocumentType | null>(null);

  const getMillis = (value: any): number => {
    try {
      if (!value) return 0;
      if (typeof value.toMillis === 'function') return value.toMillis();
      if (typeof value.toDate === 'function') return value.toDate().getTime();
      if (value instanceof Date) return value.getTime();
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    } catch {
      return 0;
    }
  };

  const signatureResidentNumber = buildResidentNumber(signatureResidentNumberFront, signatureResidentNumberBack);

  const findLatestLotteryDoc = async (params: { roomId: string; roomNumber: string; uid: string }) => {
    const { roomId, roomNumber, uid } = params;
    const lotteryRef = collection(db, 'portville_lottery_history');

    const selectLatest = (snapshot: any) => {
      if (!snapshot || snapshot.empty) return null;
      const docs = snapshot.docs
        .map((d: any) => ({ id: d.id, data: d.data() as any }))
        .sort((a: any, b: any) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));
      return docs[0] || null;
    };

    // Prefer roomId+uid (most reliable)
    try {
      const snapshot = await getDocs(query(lotteryRef, where('roomId', '==', roomId), where('uid', '==', uid)));
      const selected = selectLatest(snapshot);
      if (selected) return selected;
    } catch (err) {
      console.warn('lottery_history 조회 실패(roomId+uid):', err);
    }

    // Fallback: roomNumber+uid (legacy)
    try {
      const snapshot = await getDocs(query(lotteryRef, where('roomNumber', '==', roomNumber), where('uid', '==', uid)));
      const selected = selectLatest(snapshot);
      if (selected) return selected;
    } catch (err) {
      console.warn('lottery_history 조회 실패(roomNumber+uid):', err);
    }

    return null;
  };

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useBackToPortvilleRoomsListOnPop();

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  // 화면 크기에 따른 서명 패드 크기 조정
  const updateSignatureSize = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setSignatureSize({ width: 320, height: 160 });
    } else {
      setSignatureSize({ width: 400, height: 200 });
    }
  }, []);

  // 입주 예정일 기준 계약 종료일 계산
  // 규칙: 입주 예정일의 year + 2, day - 1 (day가 0이면 전월 말일)
  const calculateContractEndDate = useCallback((startDate: string) => {
    const match = startDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';

    const year = Number(match[1]);
    const month = Number(match[2]); // 1-12
    const day = Number(match[3]);

    const dateUtc = new Date(Date.UTC(year, month - 1, day));
    if (
      Number.isNaN(dateUtc.getTime()) ||
      dateUtc.getUTCFullYear() !== year ||
      dateUtc.getUTCMonth() !== month - 1 ||
      dateUtc.getUTCDate() !== day
    ) {
      return '';
    }

    const pad2 = (value: number) => String(value).padStart(2, '0');
    const getLastDayOfMonth = (y: number, m: number) => new Date(y, m, 0).getDate(); // m: 1-12

    let endYear = year + 2;
    let endMonth = month;
    let endDay = day - 1;

    if (endDay === 0) {
      endMonth -= 1;
      if (endMonth === 0) {
        endMonth = 12;
        endYear -= 1;
      }
      endDay = getLastDayOfMonth(endYear, endMonth);
    }

    return `${endYear}-${pad2(endMonth)}-${pad2(endDay)}`;
  }, []);

  useEffect(() => {
    if (roomId && hasAccess) {
      loadRoom();
    }
  }, [roomId, hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateSignatureSize();
    window.addEventListener('resize', updateSignatureSize);
    return () => window.removeEventListener('resize', updateSignatureSize);
  }, [updateSignatureSize]);

  // 입주 예정일 변경 시 계약 종료일 자동 계산
  useEffect(() => {
    const endDate = calculateContractEndDate(moveInDate);
    setContractEndDate(endDate);
  }, [moveInDate, calculateContractEndDate]);

  const loadRoom = async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      
      // 1. 호실 정보 조회
      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      if (!roomDoc.exists()) {
        setError('호실을 찾을 수 없습니다.');
        return;
      }

      const roomData = roomDoc.data() as PortvilleRoom;
      
      // 2. 현재 입주 기록 조회
      let currentResidency: PortvilleResidency | undefined;
      if (roomData.currentResidencyId) {
        const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
        if (residencyDoc.exists()) {
          const residencyData = residencyDoc.data();
          currentResidency = {
            id: residencyDoc.id,
            roomId: residencyData.roomId,
            roomNumber: residencyData.roomNumber,
            residentUid: residencyData.residentUid,
            residentInfo: residencyData.residentInfo,
            occupancyPeriod: residencyData.occupancyPeriod ? {
              startDate: residencyData.occupancyPeriod.startDate.toDate(),
              endDate: residencyData.occupancyPeriod.endDate.toDate()
            } : undefined,
            roommateInfo: residencyData.roommateInfo,
            vehicleInfo: residencyData.vehicleInfo,
            notes: residencyData.notes,
            signatureName: residencyData.signatureName,
            signatureAddress: residencyData.signatureAddress,
            signatureResidentNumber: residencyData.signatureResidentNumber,
            signatureDate: residencyData.signatureDate ? residencyData.signatureDate.toDate() : undefined,
            signatureUrl: residencyData.signatureUrl,
            contractSignedAt: residencyData.contractSignedAt ? residencyData.contractSignedAt.toDate() : undefined,
            moveInInspectionSignedAt: residencyData.moveInInspectionSignedAt ? residencyData.moveInInspectionSignedAt.toDate() : undefined,
            moveInInspectionSignatureUrl: residencyData.moveInInspectionSignatureUrl,
            status: residencyData.status,
            createdAt: residencyData.createdAt.toDate(),
            updatedAt: residencyData.updatedAt.toDate(),
            completedAt: residencyData.completedAt ? residencyData.completedAt.toDate() : undefined
          };
        }
      }

      // 3. 통합 뷰 생성
      const combinedRoom: PortvilleRoomWithResidency = {
          id: roomDoc.id,
        roomNumber: roomData.roomNumber,
        roomType: roomData.roomType || '호실',
        status: roomData.status,
        createdAt: (roomData.createdAt as any).toDate ? (roomData.createdAt as any).toDate() : roomData.createdAt as Date,
        updatedAt: (roomData.updatedAt as any).toDate ? (roomData.updatedAt as any).toDate() : roomData.updatedAt as Date,
        currentResidencyId: roomData.currentResidencyId,
        facilityInfo: roomData.facilityInfo
      };

      if (currentResidency) {
        combinedRoom.currentResident = currentResidency.residentInfo;
        combinedRoom.residentUid = currentResidency.residentUid;
        combinedRoom.occupancyPeriod = currentResidency.occupancyPeriod;
        combinedRoom.roommateInfo = currentResidency.roommateInfo;
        combinedRoom.vehicleInfo = currentResidency.vehicleInfo;
        combinedRoom.notes = currentResidency.notes;
        combinedRoom.signatureName = currentResidency.signatureName;
        combinedRoom.signatureAddress = currentResidency.signatureAddress;
        combinedRoom.signatureResidentNumber = currentResidency.signatureResidentNumber;
        combinedRoom.signatureDate = currentResidency.signatureDate;
        combinedRoom.signatureUrl = currentResidency.signatureUrl;
        combinedRoom.contractSignedAt = currentResidency.contractSignedAt;
        combinedRoom.moveInInspectionSignedAt = currentResidency.moveInInspectionSignedAt;
        combinedRoom.moveInInspectionSignatureUrl = currentResidency.moveInInspectionSignatureUrl;
      }
        
      setRoom(combinedRoom);
        
        // 서명자 이름 초기값 설정
      if (combinedRoom.currentResident?.name) {
        setSignatureName(combinedRoom.currentResident.name);
      }

      if (combinedRoom.signatureAddress) {
        setSignatureAddress(combinedRoom.signatureAddress);
      }

      if (combinedRoom.signatureResidentNumber) {
        const residentNumberParts = splitResidentNumber(combinedRoom.signatureResidentNumber);
        setSignatureResidentNumberFront(residentNumberParts.front);
        setSignatureResidentNumberBack(residentNumberParts.back);
      }

      // 입주 예정일 초기값 설정 (기존 계약 기간이 있으면 사용, 없으면 현재 날짜)
      if (combinedRoom.occupancyPeriod?.startDate) {
        const startDateString = combinedRoom.occupancyPeriod.startDate.toISOString().split('T')[0];
        setMoveInDate(startDateString);
      }

      // 계약 전 제출 서류(추첨 후 업로드) 조회
      try {
        setLotteryDocumentsLoading(true);
        setLotteryDocumentsError(null);
        setLotteryDocuments({});

        const lotteryRef = collection(db, 'portville_lottery_history');
        const lotterySnapshot = await getDocs(query(lotteryRef, where('roomId', '==', roomId)));

        const docs = lotterySnapshot.docs
          .map((d) => ({ id: d.id, data: d.data() }))
          .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

        const contractorUid = combinedRoom.residentUid || '';
        const preferred = contractorUid ? docs.find((d) => d.data.uid === contractorUid) : null;
        const selected = preferred || docs[0] || null;

        const documents = (selected?.data?.documents || {}) as LotteryDocuments;
        setLotteryDocuments(documents);
      } catch (lotteryError) {
        console.warn('계약 전 제출 서류 조회 실패:', lotteryError);
        setLotteryDocumentsError('계약 전 제출 서류를 불러오지 못했습니다.');
      } finally {
        setLotteryDocumentsLoading(false);
      }
    } catch (error) {
      console.error('Error loading room:', error);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const saveContract = async () => {
    if (!room || !sigCanvasRef.current || !signatureName.trim() || !signatureAddress.trim() || !signatureResidentNumber) {
      alert('서명, 서명자 이름, 주소, 주민등록번호를 모두 입력해주세요.');
      return;
    }

    if (signatureResidentNumberFront.length !== 6 || signatureResidentNumberBack.length !== 7) {
      alert('주민등록번호 13자리를 확인해주세요.');
      return;
    }

    if (sigCanvasRef.current.isEmpty()) {
      alert('서명을 입력해주세요.');
      return;
    }

    if (!room.currentResidencyId) {
      alert('현재 입주 기록을 찾을 수 없습니다.');
      return;
    }

    if (isContractCancellation && !cancellationReason.trim()) {
      alert('계약 포기 사유를 입력해주세요.');
      return;
    }

    if (!isContractCancellation && room.residentUid) {
      const currentLottery = await findLatestLotteryDoc({
        roomId: roomId!,
        roomNumber: room.roomNumber,
        uid: room.residentUid
      });
      if (currentLottery?.data?.cancellationRequestStatus === 'pending') {
        alert('사용자의 계약 포기 신청이 검토 중입니다. 포기 신청을 먼저 승인 또는 반려해주세요.');
        return;
      }
    }

	    try {
	      setSaving(true);
	      
	      // 추첨 기록에서 uid 조회
	      let contractorUid = room.residentUid || '';
	      let contractorInfo = {
	        name: room.currentResident?.name || '',
	        phoneNumber: room.currentResident?.phoneNumber || '',
	        organization: room.currentResident?.organization || ''
	      };

	      // 기존 계약 생성(관리자) 등으로 residentUid가 있는 경우 우선 사용, 없으면 추첨 기록에서 조회 시도
	      if (!contractorUid) {
	        try {
            const lotteryRef = collection(db, 'portville_lottery_history');
            const snapshot = await getDocs(query(lotteryRef, where('roomId', '==', roomId)));
            const candidates = snapshot.docs
              .map((d) => ({ id: d.id, data: d.data() as any }))
              .filter((d) => d.data?.status === 'drawn' && !d.data?.archivedAt)
              .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

            contractorUid = candidates[0]?.data?.uid || '';

            if (!contractorUid) {
              // legacy fallback
              const legacySnapshot = await getDocs(query(lotteryRef, where('roomNumber', '==', room.roomNumber)));
              const legacyCandidates = legacySnapshot.docs
                .map((d) => ({ id: d.id, data: d.data() as any }))
                .filter((d) => d.data?.status === 'drawn' && !d.data?.archivedAt)
                .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));
              contractorUid = legacyCandidates[0]?.data?.uid || '';
            }
	        } catch (lotteryError) {
	          console.warn('추첨 기록에서 uid를 가져오는 중 오류:', lotteryError);
	          // uid를 가져오지 못해도 계약은 진행
	        }
	      }

      // uid로 사용자 프로필에서 상세 정보 조회 (연락처/소속 보강)
      if (contractorUid) {
        try {
          const userProfileDoc = await getDoc(doc(db, 'user_profiles', contractorUid));
          if (userProfileDoc.exists()) {
            const userProfile: any = userProfileDoc.data();
            contractorInfo = {
              name: contractorInfo.name || userProfile.name || userProfile.displayName || '',
              phoneNumber: contractorInfo.phoneNumber || userProfile.phoneNumber || '',
              organization: contractorInfo.organization || userProfile.organization || ''
            };
            console.log('✅ 사용자 프로필에서 계약자 정보 조회 완료:', contractorInfo);
          }
        } catch (profileError) {
          console.warn('사용자 프로필 조회 중 오류:', profileError);
        }
      }
      
      // 서명 이미지를 블롭으로 변환
      const canvas = sigCanvasRef.current.getCanvas();
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/png');
      });

      // Firebase Storage에 서명 이미지 업로드
      const signatureRef = ref(storage, `contract_signatures/${roomId}_${Date.now()}.png`);
      await uploadBytes(signatureRef, blob);
      const signatureUrl = await getDownloadURL(signatureRef);

      const now = Timestamp.now();

      const consumeLatestApplication = async (params: {
        contractorUid: string;
        reason: 'contracted' | 'contract_cancelled';
        roomId: string;
        roomNumber: string;
        residencyId: string;
        at: Timestamp;
      }) => {
        const { contractorUid, reason, roomId, roomNumber, residencyId, at } = params;
        if (!contractorUid) return;

        try {
          const applicationsRef = collection(db, 'portville_applications');
          const snapshot = await getDocs(query(applicationsRef, where('uid', '==', contractorUid)));
          if (snapshot.empty) return;

          const candidates = snapshot.docs
            .map((snap) => ({ id: snap.id, data: snap.data() as any }))
            .filter((d) => ['pending', 'checked', 'approved'].includes(String(d.data.status)))
            .sort((a, b) => getMillis(b.data.createdAt) - getMillis(a.data.createdAt));

          const target = candidates[0];
          if (!target) return;

          await updateDoc(doc(db, 'portville_applications', target.id), {
            status: 'consumed',
            consumedAt: at,
            consumedReason: reason,
            consumedRoomId: roomId,
            consumedRoomNumber: roomNumber,
            consumedResidencyId: residencyId,
            updatedAt: at
          } as any);
        } catch (e) {
          console.warn('portville_applications consumed 처리 실패(계약은 유지):', e);
        }
      };

      if (isContractCancellation) {
        // 계약 포기 처리
        // 1. 입주 기록에 취소 정보 업데이트
        const cancellationData: any = {
          signatureName,
          signatureAddress,
          signatureResidentNumber,
          signatureDate: Timestamp.fromDate(new Date(signatureDate)),
          signatureUrl,
          contractCancelledAt: now,
          terminatedAt: now,
          cancellationReason,
          residentInfo: contractorInfo, // 조회된 계약자 정보로 업데이트
          status: 'terminated', // 계약 취소 상태
          updatedAt: now
        };

        if (contractorUid) {
          cancellationData.residentUid = contractorUid; // 올바른 필드명 사용
        }

        await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId!), cancellationData);

        // 2. 호실 상태를 공실로 변경
        await updateDoc(doc(db, 'portville_rooms', roomId!), {
          status: 'vacant',
          currentResidencyId: null, // 입주 기록 연결 해제
          updatedAt: now
        });

        // 3. 계약 취소 히스토리 저장
        const cancellationHistoryData: any = {
          roomId: roomId!,
          roomNumber: room.roomNumber,
          contractorInfo: contractorInfo, // 조회된 계약자 정보 사용
          signatureName,
          signatureAddress,
          signatureResidentNumber,
          signatureDate: Timestamp.fromDate(new Date(signatureDate)),
          signatureUrl,
          contractCancelledAt: now,
          cancellationReason,
          type: 'cancellation',
          createdAt: now
        };

        if (contractorUid) {
          cancellationHistoryData.contractorUid = contractorUid;
        }

        await addDoc(collection(db, 'portville_contracts'), cancellationHistoryData);

	        // 4. 추첨 기록 상태 업데이트 (drawn -> cancelled) + archived 처리
	        if (contractorUid) {
	          try {
              const latest = await findLatestLotteryDoc({
                roomId: roomId!,
                roomNumber: room.roomNumber,
                uid: contractorUid
              });
              if (latest) {
                const existing = latest.data || {};
		        await updateDoc(doc(db, 'portville_lottery_history', latest.id), {
		          status: 'cancelled',
		          cancelledAt: now,
		          suppressedAt: existing.suppressedAt || now,
		          suppressedReason: existing.suppressedReason || 'contract_cancelled',
		          linkedResidencyId: room.currentResidencyId,
		          linkedAt: existing.linkedAt || now,
		          archivedAt: existing.archivedAt || now,
		          archivedReason: existing.archivedReason || 'cancelled',
		          cancellationReason
                });
              }
	          } catch (lotteryUpdateError) {
	            console.warn('추첨 기록 상태 업데이트 중 오류:', lotteryUpdateError);
	          }
	        }

        await consumeLatestApplication({
          contractorUid,
          reason: 'contract_cancelled',
          roomId: roomId!,
          roomNumber: room.roomNumber,
          residencyId: room.currentResidencyId!,
          at: now
        });

        alert(`계약이 포기되었습니다.\n사유: ${cancellationReason}`);
        goToRoomsList();

      } else {
        // 정상 계약 처리
        // 입주 예정일 기준 계약 종료일 계산 (윤년 포함 고려)
        const computedEndYmd = calculateContractEndDate(moveInDate);
        if (!computedEndYmd) throw new Error('계약 종료일을 계산할 수 없습니다.');

        const contractStartDate = new Date(moveInDate);
        const contractEndDate = new Date(computedEndYmd);

      // 계약서 내용 생성 (사용자에게 표시되는 내용과 동일)
      const contractContent = {
        header: `임대인 부산항만공사 사장 (이하 "갑" 이라 한다) 과 임차인 ${room.currentResident?.name} (이하 "을" 이라 한다) 은 아래와 같이 임대차 계약을 체결하고 신의와 성실의 원칙에 입각하여 이를 이행한다.

소재지 : 부산광역시 강서구 신항로 96-87 (성북동 1501번지)
임대시설물: 부산 포트빌 ${room.roomNumber}호 (전용 26.5㎡ 1세대)`,
        article1: `제1조 (목적)

갑은 위 표시 부산 포트빌 각 호를 을에게 임대하고 을은 이를 임차한다.`,
        article2: `제2조 (임대보증금 및 임대기간)

①임대보증금 및 임대료, 임대기간을 다음과 같이 약정한다.

임대보증금: 800,000원
월임대료: 135,000원
임대기간: ${contractStartDate.toLocaleDateString('ko-KR')} ~ ${contractEndDate.toLocaleDateString('ko-KR')}

②임대보증금은 임대계약 체결 후 가상계좌 발급 시 을이 갑에게 납부하여야 한다.
(신한은행 140-014-896398 예금주 : (주)아스타아이비에스 )

③을은 임대료를 매월 말일까지 갑이 지정하는 은행에 선납하여야 한다.
(신한은행 140-014-896099 예금주 : (주)아스타아이비에스 )

④입주자는 열쇠지급일이 기산점이다. 입주 당월 임대료 및 관리비는 일할 계산된다.

⑤임대기간 종료 전 을의 귀책사유로 인한 계약 해지 시, 을은 위약금으로 270,000원을 납부 또는 임대보증금에서 공제한다. 위약금의 납부·공제는 갑의 을에 대한 손해배상의 청구에 영향을 미치지 아니한다.`,
        article3: `제3조 (관리비)

①관리비는 일반관리비(청소, 경비, 영선 등 입주자 보호 및 건물유지보수비용)와 공공요금(전기료, 수도료, TV시청료, 환경개선분담금 등)을 말한다.

②매월 을은 일반관리비(세대당 86,500원, 부가세별도), 통신비(신청자에 한함) 및 공공요금을 납부하여야 한다.

③을은 갑이 매월 사용량 등에 따라 산정 고지한 금액의 관리비를 매월 말일까지 납부하여야 한다. (신한 140-014-896099 예금주: (주)아스타아이비에스)

④을이 계약기간 중 임대시설물을 사용하지 않을 경우 계약전력에 의한 기본요금을 납부하여야 한다.

⑤관리비는 갑이 지정한 시설관리사업자가 갑을 대신하여 산정·고지할 수 있으며, 이 경우 관리비는 갑이 지정한 시설관리사업자에게 납부하여야 한다.

⑥을은 입주 당월에 발생하는 관리비 선납부를 위하여 선관리비(100,000원)를 관리주체에 납부 예치하여야 하며, 퇴거 시 익월 말 공과금 및 시설물 보수비 등을 정산하고 잔액을 환불하며 이자는 산정치 아니한다. 다만 제7조에 따른 계약갱신 시에는 최초 납부한 선관리비는 그대로 예치한다.`,
        article4: `제4조 (연체료)

①을이 임대차계약에 의거 갑에게 납부하여야 할 임대료 및 관리비를 소정기일까지 납부하지 않을 때에는 국유재산법을 준용하여 가산금을 징수한다.

②체납임대료 납부 시에는 연체료를 우선 변제하며, 체납임대료는 완납 시까지 연체료를 가산하여 납부하여야 한다.`,
        article5: `제5조 (임대료 및 관리비 등 조정)

갑은 물가상승 및 기타 경제적 여건에 따라 임대료 및 관리비를 조정할 수 있으며 조정할 때에는 을에게 사전 통보하여야 한다.`,
        article6: `제6조 (의무·책임)

①갑은 이 계약서 내용을 성실히 이행할 의무가 있다.

②을은 세대내 모든 시설물을 주의하여 사용하여야하며, 사용부주의로 인한 파손, 망실 시에는 변상 또는 원상복구 하여야한다.

③을의 귀책사유로 인하여 갑 또는 제3자에게 인적 또는 물적 손해를 입혔을 때에는 을은 이를 변상함은 물론 민·형사상 모든 책임을 진다.

④을은 입주 시 입주자 관리카드를 제출하여야 하며 변경사항이 있을시 에는 관리실에 입주자 변경사항을 알려야 한다.

⑤을은 관리지침 및 포트빌 관리규약에 따라 임대시설을 이용해야 하며, 본 계약 및 지침, 규약을 이행하지 않을 경우 전적인 책임을 진다.`,
        article7: `제7조 (계약갱신)

을은 계약기간 만료 후 계속 거주를 희망하는 경우 만료 1개월 전에 갑에게 임대차계약의 갱신을 신청하여야 하며, 총 4년의 범위 내에서 2년 단위로 계약을 갱신할 수 있다. 단, 1회 연장 계약 후 계약 갱신은 입주대기자가 없는 경우 추가 갱신이 가능하다.`,
        article8: `제8조 (계약해지)

①갑은 을이 다음 각 호의 1에 해당하는 경우 임대차계약을 해지할 수 있다.
  1. 허가 없이 시설물구조를 변경하거나 건물 또는 시설물을 고의적으로 훼손한 세대
  2. 임대료 및 관리비등을 3개월 이상 체납한 세대
  4. 임대보증금의 반환청구권이 제3채권자의 질권 기타 채권담보의 목적이 되었을 때
  5. 질병 및 법정 전염병에 감염되어 타인에게 전염될 염려가 있는 자
  6. 음주 및 가무 등으로 풍기를 문란하게 하여 퇴거 경고를 3회 받은 세대
  7. 시설 내 질서와 안녕을 해칠 우려가 있어 퇴거 경고를 3회 받은 세대
  8. 기타 입주자격이 부적당하다고 갑이 인정한 세대
  9. 갑의 동의 없이 제3자에게 임대시설물 전부 또는 일부를 전대한 세대

②갑, 을이 어느 일방의 사정으로 계약 해지하고자 할 때에는 1개월 이전에 상대방에게 통보하여야 한다.

③을은 임대차계약 해지 신청 시 미납금 및 고지 예정인 관리비를 정산하여야 한다.`,
        article9: `제9조 (보증금의 반환)

을이 갑에게 예치한 임대보증금은 퇴거 시에(공휴일인 경우 익영업일)에 반환한다. 이때 을이 갑에게 납부하여야 할 월 임대료 중 미납금이 있을시 임대보증금에서 우선 공제한 후 잔액에 한하여 반환하며 이자는 산정치 아니한다.`,
        article10: `제10조 (임차물의 명도)

을이 퇴거할 때에는 시설 및 비품 등에 대하여 갑이 인정할 수 있는 입주시의 상태로 원상 복구하여 갑에게 명도하여야 한다. 다만, 원상복구를 하지 않았을 경우나 입주가 불가할 정도의 상태일 경우 갑이 임의로 원상복구 할 수 있으며 그 비용을 을이 부담하지 않을 시 임대보증금에서 변제한다.`,
        article11: `제11조 (분쟁의 해결)

①본 계약과 관련하여 계약당사자간에 발생하는 이의 또는 분쟁 등을 신의성실의 원칙에 입각하여 상호 협의에 의하여 해결하도록 노력한다.

②제1항에 의한 협의가 이루어지지 아니할 때에는 상호합의를 통하여 "갑"의 사업장 소재지 관할법원의 판결 또는 「중재법」에 의한 중재에 따라 해결하되, 상호합의가 이뤄지지 않을 경우 "갑"이 지정하는 방법으로 한다.`,
        footer: `이 계약을 증명하기 위하여 갑과 을이 계약서를 작성 서명 날인하고 각각 1통씩 보관한다.`
      };

      // 1. 입주 기록 업데이트 (계약 정보 및 계약 내용 추가)
      const residencyUpdateData: any = {
        signatureName,
        signatureAddress,
        signatureResidentNumber,
        signatureDate: Timestamp.fromDate(new Date(signatureDate)),
        signatureUrl,
        contractSignedAt: now,
        occupancyPeriod: {
          startDate: Timestamp.fromDate(contractStartDate),
          endDate: Timestamp.fromDate(contractEndDate)
        },
        residentInfo: contractorInfo, // 조회된 계약자 정보로 업데이트
        contractContent: contractContent, // 계약 내용 추가
        status: 'active', // 계약 완료 후 활성 상태로 변경
        updatedAt: now
      };

      // uid가 있는 경우에만 추가
      if (contractorUid) {
        residencyUpdateData.residentUid = contractorUid; // 올바른 필드명 사용
      }

      await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId), residencyUpdateData);

      // 2. 호실 상태를 입주중으로 변경
      await updateDoc(doc(db, 'portville_rooms', roomId!), {
        status: 'occupied',
        updatedAt: now
      });

      // 3. 별도 계약 히스토리 컬렉션에도 저장 (기존 호환성 유지)
      const contractHistoryData: any = {
        roomId: roomId!,
        roomNumber: room.roomNumber,
        contractorInfo: contractorInfo, // 조회된 계약자 정보 사용
        occupancyPeriod: {
          startDate: Timestamp.fromDate(contractStartDate),
          endDate: Timestamp.fromDate(contractEndDate)
        },
        signatureName,
        signatureAddress,
        signatureResidentNumber,
        signatureDate: Timestamp.fromDate(new Date(signatureDate)),
        signatureUrl,
        contractSignedAt: now,
        contractContent: contractContent, // 동일한 계약 내용 사용
        createdAt: now
      };

      // uid가 있는 경우에만 추가
      if (contractorUid) {
        contractHistoryData.contractorUid = contractorUid;
      }

      await addDoc(collection(db, 'portville_contracts'), contractHistoryData);

	      // 4. 추첨 기록 상태 업데이트 (drawn -> contracted) + archived 처리
	      if (contractorUid) {
	        try {
            const latest = await findLatestLotteryDoc({
              roomId: roomId!,
              roomNumber: room.roomNumber,
              uid: contractorUid
            });
            if (latest) {
              const existing = latest.data || {};
	              await updateDoc(doc(db, 'portville_lottery_history', latest.id), {
	                status: 'contracted',
	                contractedAt: now,
	                suppressedAt: existing.suppressedAt || now,
	                suppressedReason: existing.suppressedReason || 'contracted',
	                linkedResidencyId: room.currentResidencyId,
	                linkedAt: existing.linkedAt || now,
	                archivedAt: existing.archivedAt || now,
	                archivedReason: existing.archivedReason || 'contracted'
              });
            }
	        } catch (lotteryUpdateError) {
	          console.warn('추첨 기록 상태 업데이트 중 오류:', lotteryUpdateError);
	          // 추첨 기록 업데이트 실패해도 계약은 완료로 처리
	        }
	      }

      await consumeLatestApplication({
        contractorUid,
        reason: 'contracted',
        roomId: roomId!,
        roomNumber: room.roomNumber,
        residencyId: room.currentResidencyId!,
        at: now
      });

              alert(`계약서 날인이 완료되었습니다.\n입주기간: ${contractStartDate.toLocaleDateString('ko-KR')} ~ ${contractEndDate.toLocaleDateString('ko-KR')} (2년)`);
      goToRoomsList();
        }
      
    } catch (error) {
      console.error('계약 처리 오류:', error);
      alert('계약 처리 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">호실 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error || '호실을 찾을 수 없습니다.'}</div>
          <button
            onClick={goToRoomsList}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            호실 관리로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 입주점검이 완료되지 않은 경우
  if (!room.moveInInspectionSignedAt) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">
            계약서 날인 전에 입주점검을 먼저 완료해주세요.
          </div>
          <div className="mt-4 flex space-x-4">
            <button
              onClick={() => navigate(`/admin/portville-rooms/move-in-inspection/${roomId}`)}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              입주점검 진행
            </button>
            <button
              onClick={goToRoomsList}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              호실 관리로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 이미 계약서가 날인된 경우
  if (room.contractSignedAt) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="text-green-800">
            이미 계약서 날인이 완료된 호실입니다.
            (완료일: {room.contractSignedAt.toLocaleString('ko-KR')})
          </div>
          <div className="mt-4 flex space-x-4">
            <button
              onClick={() => navigate(`/admin/portville-rooms/view-contract/${roomId}`)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              계약서 확인
            </button>
            <button
              onClick={goToRoomsList}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              호실 관리로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">포트빌 입주계약서 날인</h1>
        <p className="mt-2 text-sm text-gray-600">
          {room.roomNumber}호 입주점검이 완료되었습니다. 계약서에 서명해주세요.
        </p>
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-green-800">
                ✓ 입주점검 완료 ({room.moveInInspectionSignedAt?.toLocaleString('ko-KR')})
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-2">계약 전 제출 서류 (추첨 후 업로드)</h2>
        <p className="text-sm text-gray-600 mb-4">사용자가 계약 진행 전에 업로드한 서류입니다.</p>

        {lotteryDocumentsError && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
            {lotteryDocumentsError}
          </div>
        )}

        <div className="space-y-3">
          {(['identification', 'employment', 'other'] as const).map((type) => {
            const storagePath = lotteryDocuments[type];
            const isRequired = type === 'identification' || type === 'employment';
            const isDownloading = lotteryDocumentsDownloading === type;

            return (
              <div key={type} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3">
                <div className="text-sm text-gray-800">
                  <span className="font-medium">{getDocumentDisplayName(type)}</span>
                  {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                  {storagePath && (
                    <span className="ml-2 text-xs text-gray-500">
                      {getOriginalFileNameFromStoragePath(storagePath)}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  {lotteryDocumentsLoading ? (
                    <span className="text-sm text-gray-500">불러오는 중...</span>
                  ) : !storagePath ? (
                    <span className="text-sm text-gray-500">미제출</span>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!storagePath) return;
                        try {
                          setLotteryDocumentsDownloading(type);
                          await downloadStorageFile(
                            storagePath,
                            `${room.roomNumber}호_${room.currentResident?.name || '입주자'}_${getDocumentDisplayName(type)}`
                          );
                        } catch (downloadError) {
                          console.error('계약 전 제출 서류 다운로드 실패:', downloadError);
                          alert('서류 다운로드 중 오류가 발생했습니다.');
                        } finally {
                          setLotteryDocumentsDownloading(null);
                        }
                      }}
                      disabled={Boolean(lotteryDocumentsDownloading)}
                      className={`px-3 py-1.5 rounded text-sm ${
                        Boolean(lotteryDocumentsDownloading)
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isDownloading ? '다운로드 중...' : '다운로드'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* 계약서 내용 */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">부산 포트빌 임대차계약서</h2>
            <p className="text-gray-600 mt-2">호실번호: {room.roomNumber}호</p>
          </div>

          <div className="space-y-6 text-sm">
            <div> 
              <h3 className="font-semibold text-gray-900 mb-2">임대인 부산항만공사 사장 (이하 "갑" 이라 한다) 과 임차인 {room.currentResident?.name} (이하 "을" 이라 한다) 은 아래와 같이 임대차 계약을 체결하고 신의와 성실의 원칙에 입각하여 이를 이행한다.</h3>
              <p className="text-gray-700 leading-relaxed">
                소재지 : 부산광역시 강서구 신항로 96-87 (성북동 1501번지)<br/>
                임대시설물: 부산 포트빌 {room.roomNumber}호 (전용 26.5㎡ 1세대)
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제1조 (목적)</h3>
              <p className="text-gray-700 leading-relaxed">
                갑은 위 표시 부산 포트빌 각 호를 을에게 임대하고 을은 이를 임차한다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제2조 (임대보증금 및 임대기간)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①임대보증금 및 임대료, 임대기간을 다음과 같이 약정한다.<br/>
              </p>
              <table className="w-full border-collapse border border-gray-400 my-4">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2">임대보증금</td>
                    <td className="border border-gray-400 px-4 py-2">월임대료</td>
                    <td className="border border-gray-400 px-4 py-2">임대기간</td>

                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2">800,000원</td>
                    <td className="border border-gray-400 px-4 py-2">135,000원</td>
                    <td className="border border-gray-400 px-4 py-2">{new Date(moveInDate).toLocaleDateString('ko-KR')} ~ {new Date(contractEndDate).toLocaleDateString('ko-KR')} </td>

                  </tr>
                </tbody>
              </table>
              <p className="text-gray-700 leading-relaxed">
              ②임대보증금은 임대계약 체결 후 가상계좌 발급 시 을이 갑에게 납부하여야 한다. <br/>
              (신한은행 140-014-896398 예금주 : (주)아스타아이비에스 )<br/>
              ③을은 임대료를 매월 말일까지 갑이 지정하는 은행에 선납하여야 한다. <br/>
              (신한은행 140-014-896099 예금주 : (주)아스타아이비에스 )<br/>
              ④입주자는 열쇠지급일이 기산점이다. 입주 당월 임대료 및 관리비는 일할 계산된다.<br/>
              ⑤임대기간 종료 전 을의 귀책사유로 인한 계약 해지 시, 을은 위약금으로 270,000원을 납부 또는 임대보증금에서 공제한다. 위약금의 납부·공제는 갑의 을에 대한 손해배상의 청구에 영향을 미치지 아니한다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제3조 (관리비)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①관리비는 일반관리비(청소, 경비, 영선 등 입주자 보호 및 건물유지보수비용)와 공공요금(전기료, 수도료, TV시청료, 환경개선분담금 등)을 말한다. <br/>
              ②매월 을은 일반관리비(세대당 86,500원, 부가세별도), 통신비(신청자에 한함) 및 공공요금을 납부하여야 한다. <br/>
              ③을은 갑이 매월 사용량 등에 따라 산정 고지한 금액의 관리비를 매월 말일까지 납부하여야 한다. (신한 140-014-896099 예금주: (주)아스타아이비에스)<br/>
              ④을이 계약기간 중 임대시설물을 사용하지 않을 경우 계약전력에 의한 기본요금을 납부하여야 한다.<br/>
              ⑤관리비는 갑이 지정한 시설관리사업자가 갑을 대신하여 산정·고지할 수 있으며, 이 경우 관리비는 갑이 지정한 시설관리사업자에게 납부하여야 한다.<br/>
              ⑥을은 입주 당월에 발생하는 관리비 선납부를 위하여 선관리비(100,000원)를 관리주체에 납부 예치하여야 하며, 퇴거 시 익월 말 공과금 및 시설물 보수비 등을 정산하고 잔액을 환불하며 이자는 산정치 아니한다. 다만 제7조에 따른 계약갱신 시에는 최초 납부한 선관리비는 그대로 예치한다.<br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제4조 (연체료)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①을이 임대차계약에 의거 갑에게 납부하여야 할 임대료 및 관리비를 소정기일까지 납부하지 않을 때에는 국유재산법을 준용하여 가산금을 징수한다.<br/>
              ②체납임대료 납부 시에는 연체료를 우선 변제하며, 체납임대료는 완납 시까지 연체료를 가산하여 납부하여야 한다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제5조 (임대료 및 관리비 등 조정)</h3>
              <p className="text-gray-700 leading-relaxed">
              갑은 물가상승 및 기타 경제적 여건에 따라 임대료 및 관리비를 조정할 수 있으며 조정할 때에는 을에게 사전 통보하여야 한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제6조 (의무·책임)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①갑은 이 계약서 내용을 성실히 이행할 의무가 있다. <br/>
              ②을은 세대내 모든 시설물을 주의하여 사용하여야하며, 사용부주의로 인한 파손, 망실 시에는 변상 또는 원상복구 하여야한다. <br/>  
              ③을의 귀책사유로 인하여 갑 또는 제3자에게 인적 또는 물적 손해를 입혔을 때에는 을은 이를 변상함은 물론 민·형사상 모든 책임을 진다. <br/>
              ④을은 입주 시 입주자 관리카드를 제출하여야 하며 변경사항이 있을시 에는 관리실에 입주자 변경사항을 알려야 한다. <br/>
              ⑤을은 관리지침 및 포트빌 관리규약에 따라 임대시설을 이용해야 하며, 본 계약 및 지침, 규약을 이행하지 않을 경우 전적인 책임을 진다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제7조 (계약갱신)</h3>
              <p className="text-gray-700 leading-relaxed">
              을은 계약기간 만료 후 계속 거주를 희망하는 경우 만료 1개월 전에 갑에게 임대차계약의 갱신을 신청하여야 하며, 총 4년의 범위 내에서 2년 단위로 계약을 갱신할 수 있다. 단, 1회 연장 계약 후 계약 갱신은 입주대기자가 없는 경우 추가 갱신이 가능하다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제8조 (계약해지)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①갑은 을이 다음 각 호의 1에 해당하는 경우 임대차계약을 해지할 수 있다. <br/>  
                1. 허가 없이 시설물구조를 변경하거나 건물 또는 시설물을 고의적으로 훼손한 세대
                2. 임대료 및 관리비등을 3개월 이상 체납한 세대 <br/>
                4. 임대보증금의 반환청구권이 제3채권자의 질권 기타 채권담보의 목적이 되었을 때 <br/>
                5. 질병 및 법정 전염병에 감염되어 타인에게 전염될 염려가 있는 자 <br/>
                6. 음주 및 가무 등으로 풍기를 문란하게 하여 퇴거 경고를 3회 받은 세대 <br/>
                7. 시설 내 질서와 안녕을 해칠 우려가 있어 퇴거 경고를 3회 받은 세대 <br/>
                8. 기타 입주자격이 부적당하다고 갑이 인정한 세대 <br/>
                9. 갑의 동의 없이 제3자에게 임대시설물 전부 또는 일부를 전대한 세대 <br/>
              ②갑, 을이 어느 일방의 사정으로 계약 해지하고자 할 때에는 1개월 이전에 상대방에게 통보하여야 한다. <br/>
              ③을은 임대차계약 해지 신청 시 미납금 및 고지 예정인 관리비를 정산하여야 한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제9조 (보증금의 반환)</h3>
              <p className="text-gray-700 leading-relaxed">
              을이 갑에게 예치한 임대보증금은 퇴거 시에(공휴일인 경우 익영업일)에 반환한다. 이때 을이 갑에게 납부하여야 할 월 임대료 중 미납금이 있을시 임대보증금에서 우선 공제한 후 잔액에 한하여 반환하며 이자는 산정치 아니한다.              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제10조 (임차물의 명도)</h3>
              <p className="text-gray-700 leading-relaxed">
              을이 퇴거할 때에는 시설 및 비품 등에 대하여 갑이 인정할 수 있는 입주시의 상태로 원상 복구하여 갑에게 명도하여야 한다. 다만, 원상복구를 하지 않았을 경우나 입주가 불가할 정도의 상태일 경우 갑이 임의로 원상복구 할 수 있으며 그 비용을 을이 부담하지 않을 시 임대보증금에서 변제한다.            </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제11조 (분쟁의 해결)</h3>
              <p className="text-gray-700 leading-relaxed">
              ①본 계약과 관련하여 계약당사자간에 발생하는 이의 또는 분쟁 등을 신의성실의 원칙에 입각하여 상호 협의에 의하여 해결하도록 노력한다. <br/>  
              ②제1항에 의한 협의가 이루어지지 아니할 때에는 상호합의를 통하여 “갑”의 사업장 소재지 관할법원의 판결 또는 「중재법」에 의한 중재에 따라 해결하되, 상호합의가 이뤄지지 않을 경우 “갑”이 지정하는 방법으로 한다. <br/>            </p>
            </div>

            

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">이 계약을 증명하기 위하여 갑과 을이 계약서를 작성 서명 날인하고 각각 1통씩 보관한다.</h3>

            </div>

          </div>

          {/* 서명 영역 */}
          <div className="mt-12 border-t pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 서명자 정보 입력 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">서명자 정보</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      서명자 성명
                    </label>
                    <input
                      type="text"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="서명자 이름을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      서명자 주소
                    </label>
                    <input
                      type="text"
                      value={signatureAddress}
                      onChange={(e) => setSignatureAddress(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="주소를 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      서명자 주민등록번호
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={6}
                        value={signatureResidentNumberFront}
                        onChange={(e) => setSignatureResidentNumberFront(sanitizeResidentNumberPart(e.target.value, 6))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="앞 6자리"
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={7}
                        value={signatureResidentNumberBack}
                        onChange={(e) => setSignatureResidentNumberBack(sanitizeResidentNumberPart(e.target.value, 7))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="뒤 7자리"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      숫자만 입력하면 됩니다. 저장 시에는 000000-0000000 형식으로 통일됩니다.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      입주 예정일
                    </label>
                    <input
                      type="date"
                      value={moveInDate}
                      onChange={(e) => setMoveInDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      입주 예정일부터 2년 계약됩니다. (종료일: {new Date(contractEndDate).toLocaleDateString('ko-KR')})
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      서명 날짜
                    </label>
                    <input
                      type="date"
                      value={signatureDate}
                      onChange={(e) => setSignatureDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* 계약 포기 옵션 */}
                  <div className="mt-6 p-4 border border-orange-200 rounded-md bg-orange-50">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="contractCancellation"
                        checked={isContractCancellation}
                        onChange={(e) => setIsContractCancellation(e.target.checked)}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <label htmlFor="contractCancellation" className="text-sm font-medium text-orange-700">
                        계약을 포기합니다
                      </label>
                    </div>
                    {isContractCancellation && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-orange-700 mb-2">
                          포기 사유
                        </label>
                        <textarea
                          value={cancellationReason}
                          onChange={(e) => setCancellationReason(e.target.value)}
                          className="w-full px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="계약 포기 사유를 입력해주세요"
                          rows={3}
                        />
                        <p className="mt-1 text-xs text-orange-600">
                          계약 포기 시 서명 후 해당 호실은 공실 상태로 변경됩니다.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 서명 패드 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">서명</h3>
                <div 
                  className="border-2 border-gray-300 rounded-md overflow-hidden bg-white"
                  style={{ 
                    width: `${signatureSize.width}px`, 
                    height: `${signatureSize.height}px`,
                    maxWidth: '100%'
                  }}
                >
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    canvasProps={{
                      width: signatureSize.width,
                      height: signatureSize.height,
                      className: 'signature-canvas',
                      style: { 
                        width: '100%', 
                        height: '100%',
                        display: 'block'
                      }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={clearSignature}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  서명 지우기
                </button>
                  <span className="text-xs text-gray-500 self-center">
                    서명 영역: {signatureSize.width}×{signatureSize.height}px
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 계약 완료 버튼 */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={goToRoomsList}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              disabled={saving}
            >
              취소
            </button>
            <button
              onClick={saveContract}
              disabled={saving}
              className={`px-6 py-2 text-white rounded-md disabled:opacity-50 ${
                isContractCancellation 
                  ? 'bg-orange-600 hover:bg-orange-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {saving 
                ? '처리 중...' 
                : isContractCancellation 
                ? '계약 포기 및 서명 완료' 
                : '계약서 날인 완료'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractSignaturePage; 
